import React from "react";

import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Progress } from "@/src/components/ui/progress";
import { cn } from "@/src/lib/utils";

import type { JSX } from "react";

import type { LivePhase, LiveState } from "../../utils/liveView";

interface LivePhaseGaugeProps {
  phase: LivePhase;
  state: LiveState;
  /** Elapsed share of the live (0 to 1), shown on the on-air segment. */
  progress?: number;
  remainingMinutes?: number;
}

const PHASES: LivePhase[] = ["preparation", "connexion", "antenne", "termine"];

function getDetailKey(
  phase: LivePhase,
  state: LiveState,
  remainingMinutes?: number,
): string | null {
  if (phase === "antenne") {
    if (state === "signalLost") return "liveRegie_gauge_signalLost";
    return remainingMinutes === undefined ? null : "liveRegie_gauge_remaining";
  }
  if (phase === "connexion") {
    if (state === "provisioningFailed") return "liveRegie_gauge_blocked";
    if (state === "provisioning") return "liveRegie_gauge_provisioning";
    if (state === "waitingSignal") return "liveRegie_gauge_waitingSignal";
    if (state === "readyToAir" || state === "publishing") {
      return "liveRegie_gauge_verified";
    }
    return null;
  }
  if (phase === "termine") {
    if (state === "stopping") return "liveRegie_gauge_finalizing";
    if (state === "replayAvailable") return "liveRegie_gauge_replayToCreate";
    if (state === "replayCreated") return "liveRegie_gauge_replayCreated";
    return null;
  }
  return "liveRegie_gauge_inProgress";
}

function LivePhaseGauge({
  phase,
  state,
  progress,
  remainingMinutes,
}: LivePhaseGaugeProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const currentIndex = PHASES.indexOf(phase);
  const detailKey = getDetailKey(phase, state, remainingMinutes);
  // The last phase is complete once the stop is finalized.
  const isFinished = phase === "termine" && state !== "stopping";

  return (
    <ol aria-label={t("liveRegie_gauge")} className="grid grid-cols-4 gap-2">
      {PHASES.map((item, index) => {
        const isCurrent = index === currentIndex;
        const isDone = index < currentIndex || (isCurrent && isFinished);
        const showProgress =
          isCurrent && item === "antenne" && progress !== undefined;

        return (
          <li
            key={item}
            aria-current={isCurrent ? "step" : undefined}
            className="flex min-w-0 flex-col gap-1.5"
          >
            {showProgress ? (
              <Progress className="h-1.5" value={Math.round(progress * 100)} />
            ) : (
              <div
                className={cn(
                  "h-1.5 rounded-full",
                  "bg-muted",
                  isCurrent && "bg-primary/50",
                  isDone && "bg-primary",
                )}
              />
            )}
            <span
              className={cn(
                "flex items-center gap-1 truncate text-xs",
                isCurrent
                  ? "text-foreground font-semibold"
                  : "text-muted-foreground",
              )}
            >
              {isDone ? <Check aria-hidden="true" className="size-3" /> : null}
              {t(`liveRegie_phase_${item}`)}
              {isCurrent && detailKey ? (
                <span className="text-muted-foreground truncate font-normal">
                  {" · "}
                  {t(detailKey, { minutes: remainingMinutes })}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default LivePhaseGauge;
